import { c as _c } from "react/compiler-runtime";
import { getCountryFromCode } from '@onefootprint/core';
import { IcoClock40 } from '@onefootprint/icons';
import type { Iso3166TwoDigitCountryCode } from '@onefootprint/request-types';
import { Button } from '@onefootprint/ui';
import upperFirst from 'lodash/upperFirst';
import type React from 'react';
import { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { getLogger } from '@/idv2/utils/logger';
import { DESKTOP_INTERACTION_BOX_HEIGHT } from '../../constants';
import useProcessImage, { ImageFileOrUrlProcessingError, isProcessingResultError } from '../../hooks/use-process-image';
import { type IdDocImageErrorType, IdDocImageUploadError, type ReceivedImagePayload } from '../../types';
import handleFileUpload from '../../utils/handle-file-upload';
import DesktopHeader from '../desktop-header/desktop-header';
import ErrorComponent from '../error';
import HeaderTitle from '../header-title';
import Loading from '../loading';
import DraggableInputField from './draggable-input-field';
const {
  logWarn
} = getLogger({
  name: 'idv2',
  location: 'desktop-photo-prompt'
});
type DesktopPhotoPromptProps = {
  docName?: string;
  sideName?: string;
  country?: Iso3166TwoDigitCountryCode;
  isRetry?: boolean;
  errors?: IdDocImageErrorType[];
  hasBadConnectivity?: boolean;
  allowPdf: boolean;
  onUploadSuccess: (arg: ReceivedImagePayload) => void;
  onUploadError: (errs: IdDocImageUploadError[]) => void;
  showCameraFallbackText?: boolean;
  isSelfie?: boolean;
};
const DesktopPhotoPrompt = t0 => {
  const $ = _c(75);
  const {
    docName,
    sideName,
    country,
    isRetry,
    errors,
    hasBadConnectivity,
    allowPdf,
    onUploadSuccess,
    onUploadError,
    showCameraFallbackText,
    isSelfie
  } = t0;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = {
      keyPrefix: "document-flow.components.desktop-photo-prompt"
    };
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  const {
    t
  } = useTranslation("idv2", t1);
  const uploadPhotoRef = useRef(undefined);
  let t2;
  if ($[1] !== allowPdf) {
    t2 = {
      allowPdf
    };
    $[1] = allowPdf;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  const {
    processImageFile,
    acceptedFileFormats
  } = useProcessImage(t2);
  const [hasError, setHasError] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  let t3;
  if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = () => {
      setIsLoading(false);
    };
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  const onProcessingDone = t3;
  let t4;
  if ($[4] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = () => setHasError(true);
    $[4] = t4;
  } else {
    t4 = $[4];
  }
  const handleError = t4;
  let t5;
  if ($[5] !== hasBadConnectivity || $[6] !== onUploadError || $[7] !== onUploadSuccess || $[8] !== processImageFile || $[9] !== showCameraFallbackText) {
    t5 = async file => {
      setIsLoading(true);
      setHasError(false);
      const processResult = await processImageFile(file, hasBadConnectivity);
      if (isProcessingResultError(processResult)) {
        const errorType = processResult.error;
        onProcessingDone();
        onUploadError([errorType === ImageFileOrUrlProcessingError.heicNotSupported ? IdDocImageUploadError.heicNotSupported : IdDocImageUploadError.unknownUploadError]);
        logWarn("The uploaded image could not be processed due to an unknown error.");
        return;
      }
      onUploadSuccess({
        imageFile: processResult.file,
        captureKind: "upload",
        extraCompressed: processResult.extraCompressed,
        forcedUpload: !!showCameraFallbackText
      });
      onProcessingDone();
    };
    $[5] = hasBadConnectivity;
    $[6] = onUploadError;
    $[7] = onUploadSuccess;
    $[8] = processImageFile;
    $[9] = showCameraFallbackText;
    $[10] = t5;
  } else {
    t5 = $[10];
  }
  const handleImage = t5;
  let t6;
  if ($[11] !== acceptedFileFormats || $[12] !== handleImage || $[13] !== onUploadError) {
    t6 = ev => {
      const {
        files
      } = ev.target;
      const result = handleFileUpload({
        acceptedFileFormats,
        files
      });
      if (!result.ok) {
        onUploadError(result.errors);
        return;
      }
      handleImage(result.file);
    };
    $[11] = acceptedFileFormats;
    $[12] = handleImage;
    $[13] = onUploadError;
    $[14] = t6;
  } else {
    t6 = $[14];
  }
  const handleImageUpload = t6;
  const countryName = getCountryFromCode(country)?.label;
  let t7;
  if ($[15] === Symbol.for("react.memo_cache_sentinel")) {
    t7 = () => {
      uploadPhotoRef.current?.click();
    };
    $[15] = t7;
  } else {
    t7 = $[15];
  }
  const handleUpload = t7;
  const onFileInputClick = _temp;
  let acceptedFormatsForHint;
  if ($[16] !== acceptedFileFormats) {
    acceptedFormatsForHint = ["JPG", "PNG", "WEBP", "BMP"];
    if (acceptedFileFormats.includes("pdf") || acceptedFileFormats.includes("application/pdf")) {
      acceptedFormatsForHint.push("PDF");
    }
    $[16] = acceptedFileFormats;
    $[17] = acceptedFormatsForHint;
  } else {
    acceptedFormatsForHint = $[17];
  }
  let t8;
  if ($[18] !== docName) {
    t8 = docName ? upperFirst(docName) : docName;
    $[18] = docName;
    $[19] = t8;
  } else {
    t8 = $[19];
  }
  let t9;
  if ($[20] !== sideName) {
    t9 = sideName ? upperFirst(sideName) : sideName;
    $[20] = sideName;
    $[21] = t9;
  } else {
    t9 = $[21];
  }
  let t10;
  if ($[22] !== country || $[23] !== isSelfie || $[24] !== t8 || $[25] !== t9) {
    t10 = <DesktopHeader docName={t8} country={country} sideName={t9} isSelfie={isSelfie} />;
    $[22] = country;
    $[23] = isSelfie;
    $[24] = t8;
    $[25] = t9;
    $[26] = t10;
  } else {
    t10 = $[26];
  }
  let t11;
  if ($[27] !== isLoading || $[28] !== showCameraFallbackText || $[29] !== t) {
    t11 = showCameraFallbackText && !isLoading && <><HeaderTitle title={t(_temp2)} display="flex" flexDirection="column" icon={IcoClock40} /><div className="text-body-2 text-quaternary">{t(_temp3)}</div></>;
    $[27] = isLoading;
    $[28] = showCameraFallbackText;
    $[29] = t;
    $[30] = t11;
  } else {
    t11 = $[30];
  }
  let t12;
  if ($[31] !== country || $[32] !== countryName || $[33] !== docName || $[34] !== errors || $[35] !== isRetry || $[36] !== sideName) {
    t12 = isRetry && <div className="p-6"><ErrorComponent errors={errors ?? [{
        errorType: "unknown_error"
      }]} sideName={sideName} docName={docName} countryName={countryName ?? country} /></div>;
    $[31] = country;
    $[32] = countryName;
    $[33] = docName;
    $[34] = errors;
    $[35] = isRetry;
    $[36] = sideName;
    $[37] = t12;
  } else {
    t12 = $[37];
  }
  let t13;
  if ($[38] !== isLoading || $[39] !== isRetry) {
    t13 = !isRetry && isLoading && <Loading step="process" />;
    $[38] = isLoading;
    $[39] = isRetry;
    $[40] = t13;
  } else {
    t13 = $[40];
  }
  let t14;
  if ($[41] !== acceptedFormatsForHint || $[42] !== allowPdf || $[43] !== isLoading || $[44] !== isRetry || $[45] !== t) {
    t14 = !isRetry && !isLoading && <><div className="flex"><div className="text-label-2 text-accent">{t(_temp4)}</div><div className="text-body-2 text-quaternary"> {t(_temp5)}</div></div>{!allowPdf && <div className="text-body-2 text-quaternary">{t(_temp6, {
          accepted: acceptedFormatsForHint.join(", ")
        })}</div>}</>;
    $[41] = acceptedFormatsForHint;
    $[42] = allowPdf;
    $[43] = isLoading;
    $[44] = isRetry;
    $[45] = t;
    $[46] = t14;
  } else {
    t14 = $[46];
  }
  let t15;
  if ($[47] !== acceptedFileFormats) {
    t15 = acceptedFileFormats.join(",");
    $[47] = acceptedFileFormats;
    $[48] = t15;
  } else {
    t15 = $[48];
  }
  let t16;
  if ($[49] !== handleImageUpload || $[50] !== t15) {
    t16 = <input ref={uploadPhotoRef as React.RefObject<HTMLInputElement | null>} type="file" accept={t15} onClick={onFileInputClick} onChange={handleImageUpload} className="hidden" />;
    $[49] = handleImageUpload;
    $[50] = t15;
    $[51] = t16;
  } else {
    t16 = $[51];
  }
  let t17;
  if ($[52] !== allowPdf || $[53] !== handleImage || $[54] !== hasError || $[55] !== isLoading || $[56] !== onUploadError || $[57] !== t11 || $[58] !== t12 || $[59] !== t13 || $[60] !== t14 || $[61] !== t16) {
    t17 = <DraggableInputField height={DESKTOP_INTERACTION_BOX_HEIGHT} onComplete={handleImage} isLoading={isLoading} hasError={hasError} onUploadError={onUploadError} allowPdf={allowPdf}>{t11}{t12}{t13}{t14}{t16}</DraggableInputField>;
    $[52] = allowPdf;
    $[53] = handleImage;
    $[54] = hasError;
    $[55] = isLoading;
    $[56] = onUploadError;
    $[57] = t11;
    $[58] = t12;
    $[59] = t13;
    $[60] = t14;
    $[61] = t16;
    $[62] = t17;
  } else {
    t17 = $[62];
  }
  const t18 = isRetry ? handleUpload : handleError;
  const t19 = `doc:${isRetry ? "upload-photo-retry" : "continue"}`;
  let t20;
  if ($[63] !== isRetry || $[64] !== t) {
    t20 = isRetry ? t(_temp7) : t(_temp8);
    $[63] = isRetry;
    $[64] = t;
    $[65] = t20;
  } else {
    t20 = $[65];
  }
  let t21;
  if ($[66] !== isLoading || $[67] !== t18 || $[68] !== t19 || $[69] !== t20) {
    t21 = <Button fullWidth={true} disabled={isLoading} onClick={t18} size="large" data-dd-action-name={t19}>{t20}</Button>;
    $[66] = isLoading;
    $[67] = t18;
    $[68] = t19;
    $[69] = t20;
    $[70] = t21;
  } else {
    t21 = $[70];
  }
  let t22;
  if ($[71] !== t10 || $[72] !== t17 || $[73] !== t21) {
    t22 = <div className="flex flex-col gap-6 max-md:px-2">{t10}{t17}{t21}</div>;
    $[71] = t10;
    $[72] = t17;
    $[73] = t21;
    $[74] = t22;
  } else {
    t22 = $[74];
  }
  return t22;
};
export default DesktopPhotoPrompt;
function _temp(ev_0) {
  ev_0.stopPropagation();
}
function _temp2($) {
  return $["camera-fallback-text"].title;
}
function _temp3($_0) {
  return $_0["camera-fallback-text"].subtitle;
}
function _temp4($_1) {
  return $_1["upload-link-button"].title;
}
function _temp5($_2) {
  return $_2["drag-drop-text"]["line-1"];
}
function _temp6($_3) {
  return $_3["drag-drop-text"]["line-2"];
}
function _temp7($_4) {
  return $_4["choose-different-file"];
}
function _temp8($_5) {
  return $_5.continue;
}